import { type Maybe } from '@adornis/base/utilTypes.js';
/* eslint-disable complexity */
import { A } from '@adornis/base/env-info.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Query } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { selectionSet, type BaseQLSelectionSet, type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { mailTemplateDefinitions, type MailTemplateDefinition } from '@adornis/mails/db/mail-template-definition.js';
import { MailTemplate, type MailVarContents } from '@adornis/mails/db/mail-template.js';
import type { BuildEmailOptions } from '@adornis/mails/db/templates.js';
import { html, nothing } from 'lit';
import type { DateTime } from 'luxon';
import { switchMap } from 'rxjs';
import { ParticipationState } from '../enums/enums.js';
import {
  buildConfirmMail,
  buildForgotPassword,
  buildLoginLink,
  buildOTP,
  buildProductCard,
  buildRegistrationsHTML,
  getAppointmentsForParticipation,
} from '../mails/mail-helpers.js';
import { getPluginForScope } from '../plugins/plugin.js';
import { isDefined } from '../util.js';
import { ColorDefinitions } from './ColorDefinitions.js';
import { Participation } from './Participation.js';
import type { Einzelberatung } from './einzelberatung.js';
import { GlobalSetting } from './global-setting.js';
import { Institution } from './institution.js';
import { Product } from './products.js';
import { Setting, SettingColorDefintionProp } from './settings.js';
import type { User } from './user.js';
import type { Workshop } from './workshop.js';

export enum MailTemplateType {
  NUTZER_TEILNAHME = 'Nutzer_Teilnahme',
  NUTZER_TEILNAHME_OHNE_ZERTIFIKAT = 'Nutzer_Teilnahme_ohne_Zertifikat',
  NUTZER_TEILNAHME_AUFGEHOBEN = 'Nutzer_Teilnahme_Aufgehoben',
  BESTAETIGUNG_WORKSHOP_REGISTRIERUNG = 'Bestätigung_Workshop_Registrierung',
  SPERRUNG = 'Sperrung_durch_Einrichtung',
  SPERRUNG_AUFGEHOBEN = 'Sperrung_aufgehoben',
  NACHRUECKPLATZ_VERFUEGBAR = 'Nachrückplatz_verfügbar',
  NACHRUECKPLATZ_ERHALTEN = 'Nachrückplatz_erhalten',
  WARTELISTE_VORRUECK = 'Warteliste_vorrueck',
  VERANSTALTUNGSEINTRAGUNG_DURCH_ADMIN = 'Veranstaltungseintragung_durch_Admin',
  ABMELDUNG = 'Abmeldung',
  ABMELDUNG_DURCH_EINRICHTUNG = 'Abmeldung_durch_Einrichtung',
  PRODUCT_CANCELED = 'Product_canceled',
  PRODUCT_DECANCELED = 'Product_Decanceled',
  ERINNERUNG = 'Erinnerung',
  ERINNERUNG_APPOINTMENT = 'Erinnerung_Appointment',
  MEETING_INFOS = 'Meeting Informationen',
  OTP = 'OTP-Code',
  FORGOT_PASSWORD = 'Passwort vergessen',
  PRODUKT_LOESCHUNG = 'Produkt_Loeschung',
  PRODUCT_CHANGED = 'Product_Änderungen',
  INFO_NEWSLETTER = 'INFO_NEWSLETTER',
  NEWSLETTER_OPTIN = 'Newsletter_Opt_In',
  FREE_FORM = 'FREE_FORM',
  IKH_INVOICE_USER = 'plugin::payment::ikh::IKH-Rechnung_User',
  IKH_INVOICE_INSTITUTION = 'plugin::payment::ikh::IKH-Rechnung_Institution',
  SD_INVOICE_USER = 'plugin::payment::sd::SD-Rechnung_User',
  SD_INVOICE_INSTITUTION = 'plugin::payment::sd::SD-Rechnung_Institution',
  DEMO_INVOICE_USER = 'plugin::payment::demo::IKH-Rechnung_User',
  DEMO_INVOICE_INSTITUTION = 'plugin::payment::demo::IKH-Rechnung_Institution',
  LECTURER_REGISTRATION = 'Lecturer_Registration',
  LECTURER_ADDED_TO_PRODUCT = 'Lecturer_Added_to_Workshop',
  NEW_WORKSHOP_DOCUMENTS = 'New_Workshop_Documents',
  WORKSHOP_CHANGED = 'WORKSHOP_CHANGED',
}

export enum MailTemplateVars {
  TITLE = 'title',
  FIRST_NAME = 'firstname',
  LAST_NAME = 'lastname',
  CHANGED_REGISTRATIONS = 'changed-registrations',
  CHANGED_REGISTRATIONS_EN = 'changed-registrations-en',
  OTHER_REGISTRATIONS = 'other-registrations',
  PRODUCT_CARD = 'termin-card',
  APPOINTMENT_NAME = 'appointment-name',
  APPOINTMENT_START = 'appointment-start',
  APPOINTMENT_END = 'appointment-end',
  APPOINTMENT_LOCATION = 'appointment-location',
  LOGIN_LINK = 'login-link',
  OTP = 'otp-code',
  CONFIRM_MAIL_LINK = 'mail-confirm-link',
  FORGOT_PASSWORD_LINK = 'forgot-password-link',
  EVENT_TITLE = 'event-title',
  PRODUCT_NAME = 'product-name',
  EVENT_LOCATION = 'event-location',
  EVENT_DATES = 'event-dates',
  EVENT_LEAD_DAYS = 'event-lead-days', // (Calendar Days till start of Event/Appointment)
  INSTITUTION_NAME = 'institution-name',
  PRODUCT_CHANGES = 'product-changes',
  PRODUCT_CHANGES_EN = 'product-changes-en',
  IKH_INVOICE_USER = 'ikh-invoice-User',
  IKH_INVOICE_INSTITUTION = 'ikh-invoice-institution',
  SD_INVOICE_USER = 'sd-invoice-User',
  SD_INVOICE_INSTITUTION = 'sd-invoice-institution',
  AUTOMATIC_MOVE_UP_DAYS = 'automatic-move-up-days',
  BLOCK_SIGNOFF_DAYS = 'block-signoff-days',
  WORKSHOP_NAME = 'workshop-name',
  WORKSHOP_TERMINE = 'workshop-termine',
  WORKSHOP_LOCATION = 'workshop-location',
  BOOKABLE_WORKSHOPS = 'bookable-workshops',
  UNSUBSCRIBE_FROM_NEWSLETTER_LINK = 'unsubscribe-from-newsletter-link',
  NEWSLETTER_OPTIN_LINK = 'newsletter-optin-link',
  DOCUMENT_LINKS = 'document-links',
  MEETING_LINK = 'meeting-link',
  EVALUATION_LINK = 'evaluation-link',
  EVALUATION_CODE = 'evaluation-code',
}

@Entity()
export class GlobalMailTemplate extends MailTemplate {
  static override _class = 'GlobalMailTemplate';

  static override buildMailOptions: Partial<BuildEmailOptions> = {
    title: 'Buchungsheld Benachrichtigungsmail',
    footer: html`
      <div class="section" style="margin-top: 20px; font-size: 0.8em;">
        <div style="width: 100%">
          <div><a style="text-decoration: none;" href=${`${A.absoluteUrl()}impressum`}>Impressum</a></div>
          <div><a style="text-decoration: none;" href=${`${A.absoluteUrl()}datenschutz`}>Datenschutz</a></div>
        </div>
      </div>
    `,
    colorConfig: {
      mainBorder: 'none',
      mainBack: DesignSystem.currentTheme.getValue().colors.neutral.shade(50),
      wrapperBack: 'transparent',
    },
  };

  @Field(type => Boolean, { default: v => v ?? true })
  emailNotification: Maybe<boolean>;

  @Field(type => Boolean, { default: v => v ?? false })
  whatsappNotification: Maybe<boolean>;

  static override getMailHint(emailType: MailTemplateType): Renderable {
    switch (emailType) {
      case MailTemplateType.OTP:
        return 'Mail wird verschickt, wenn sich ein Nutzer bei der Registrierung mit einem 4-Stelligen Code authentifizieren muss.';
      case MailTemplateType.NUTZER_TEILNAHME_AUFGEHOBEN:
        return 'Mail wird manuell durch den Admin verschickt nachdem ein Teilnehmer an einem Workshop nichtmehr teilgenommen hat.';
      case MailTemplateType.WARTELISTE_VORRUECK:
        return 'Mail wird verschickt, wenn sich der Wartelistenplatz ändert.';
      case MailTemplateType.NUTZER_TEILNAHME:
      case MailTemplateType.NUTZER_TEILNAHME_OHNE_ZERTIFIKAT:
        return 'Mail wird verschickt nachdem ein Teilnehmer an einem Workshop teilgenommen hat.';
      case MailTemplateType.BESTAETIGUNG_WORKSHOP_REGISTRIERUNG:
        return 'Bestätigungsmail nach erfolgreichem Registrieren zu Workshops.';
      case MailTemplateType.PRODUCT_CANCELED:
        return 'Mail wird verschickt, wenn ein Event abgesagt wurde.';
      case MailTemplateType.PRODUCT_DECANCELED:
        return 'Mail wird verschickt, wenn ein ein ehamls abgesages Event wieder aktiviert wurde.';
      case MailTemplateType.SPERRUNG:
        return 'Mail wird verschickt, wenn ein Nutzer gesperrt wurde.';
      case MailTemplateType.SPERRUNG_AUFGEHOBEN:
        return 'Mail wird verschickt, wenn ein Nutzer entsperrt wurde.';
      case MailTemplateType.NACHRUECKPLATZ_VERFUEGBAR:
        return 'Mail wird verschickt, wenn ein für einen wartenden Nuter die Möglichkeit besteht dem Workshop beizutreten';
      case MailTemplateType.NACHRUECKPLATZ_ERHALTEN:
        return 'Mail wird verschickt, wenn ein Teilnehmer durch die Abmeldung anderer Teilnehmer einen Platz in eime Workshop erhalten hat.';
      case MailTemplateType.VERANSTALTUNGSEINTRAGUNG_DURCH_ADMIN:
        return 'Mail wird verschickt, wenn ein Admin einen Teilnehmern manuell zu einem Workshop zulässt.';
      case MailTemplateType.ABMELDUNG:
        return 'Bestätigungsmail, nachdem der Nutzer sich selbst von einem Workshop abgemeldet hat.';
      case MailTemplateType.ABMELDUNG_DURCH_EINRICHTUNG:
        return 'Mail wird verschickt, wenn ein Admin einen Teilnehmer von einer Veranstaltung abmeldet.';
      case MailTemplateType.ERINNERUNG:
      case MailTemplateType.MEETING_INFOS:
        return 'Wird einige Zeit vor Start des Workshops an alle Teilnehmer verschickt.';
      case MailTemplateType.ERINNERUNG_APPOINTMENT:
        return 'Wird für jeden Termin einige Zeit vor Beginn an alle Teilnehmer verschickt.';
      case MailTemplateType.FORGOT_PASSWORD:
        return `Wird verschickt, wenn ein Nuitzer sein Passwort vergessen hat und ein neues setzen möchte.`;
      case MailTemplateType.PRODUKT_LOESCHUNG:
        return 'Wenn ein Produkt gelöscht wird, wird diese Mail an alle Teilnehmer verschickt.';
      case MailTemplateType.PRODUCT_CHANGED:
        return 'Wenn ein Produkt geändert wird, wird diese Mail an alle Teilnehmer verschickt.';
      case MailTemplateType.IKH_INVOICE_USER:
      case MailTemplateType.SD_INVOICE_USER:
      case MailTemplateType.DEMO_INVOICE_USER:
        return 'Mit dieser Mail werden Nutzern Rechnungen übermittelt.';
      case MailTemplateType.IKH_INVOICE_INSTITUTION:
      case MailTemplateType.SD_INVOICE_INSTITUTION:
      case MailTemplateType.DEMO_INVOICE_INSTITUTION:
        return 'Diese Mail wird der Rechnungstelle gesendet wenn einem Nutzer eine Rechnung ausgestellt wurde.';
      case MailTemplateType.INFO_NEWSLETTER:
        return 'Diese Mail wird alle 14 Tage an alle Teilnehmer gesendet, um über neue Angebote zu informieren';
      case MailTemplateType.NEWSLETTER_OPTIN:
        return 'Diese Mail wird an alle Teilnehmer versendet, wenn sie sich für den Newsletter anmelden.';
      case MailTemplateType.LECTURER_REGISTRATION:
        return 'Diese Mail wird an Dozierende versendet, wenn ihr Nutzeraccount angelegt wird.';
      case MailTemplateType.LECTURER_ADDED_TO_PRODUCT:
        return 'Diese Mail wird an Dozierende versendet wenn sie zu einem Produkt hinzugefügt wurden.';
      case MailTemplateType.NEW_WORKSHOP_DOCUMENTS:
        return 'Diese Mail wird an Nutzer versendet wenn einem Workshop ein neues Dokument hinzugefügt wurde.';
      default:
        return '';
    }
  }

  static override getAvailableVariables(emailType: MailTemplateType) {
    switch (emailType) {
      case MailTemplateType.ABMELDUNG:
      case MailTemplateType.ABMELDUNG_DURCH_EINRICHTUNG:
      case MailTemplateType.BESTAETIGUNG_WORKSHOP_REGISTRIERUNG:
      case MailTemplateType.NACHRUECKPLATZ_VERFUEGBAR:
      case MailTemplateType.NACHRUECKPLATZ_ERHALTEN:
      case MailTemplateType.NUTZER_TEILNAHME_AUFGEHOBEN:
      case MailTemplateType.VERANSTALTUNGSEINTRAGUNG_DURCH_ADMIN:
      case MailTemplateType.WARTELISTE_VORRUECK:
        return [
          MailTemplateVars.TITLE,
          MailTemplateVars.FIRST_NAME,
          MailTemplateVars.LAST_NAME,
          MailTemplateVars.CHANGED_REGISTRATIONS,
          MailTemplateVars.CHANGED_REGISTRATIONS_EN,
          MailTemplateVars.OTHER_REGISTRATIONS,
          MailTemplateVars.LOGIN_LINK,
          MailTemplateVars.EVENT_TITLE,
          MailTemplateVars.EVENT_DATES,
          MailTemplateVars.EVENT_LEAD_DAYS,
        ];
      case MailTemplateType.NUTZER_TEILNAHME:
      case MailTemplateType.NUTZER_TEILNAHME_OHNE_ZERTIFIKAT:
        return [
          MailTemplateVars.TITLE,
          MailTemplateVars.FIRST_NAME,
          MailTemplateVars.LAST_NAME,
          MailTemplateVars.EVENT_TITLE,
          MailTemplateVars.LOGIN_LINK,
          MailTemplateVars.EVALUATION_LINK,
          MailTemplateVars.EVALUATION_CODE,
        ];
      case MailTemplateType.PRODUCT_CANCELED:
      case MailTemplateType.PRODUCT_DECANCELED:
        return [
          MailTemplateVars.TITLE,
          MailTemplateVars.FIRST_NAME,
          MailTemplateVars.LAST_NAME,
          MailTemplateVars.LOGIN_LINK,
          MailTemplateVars.WORKSHOP_NAME,
          MailTemplateVars.WORKSHOP_TERMINE,
          MailTemplateVars.WORKSHOP_LOCATION,
        ];
      case MailTemplateType.NEWSLETTER_OPTIN:
        return [
          MailTemplateVars.TITLE,
          MailTemplateVars.FIRST_NAME,
          MailTemplateVars.LAST_NAME,
          MailTemplateVars.NEWSLETTER_OPTIN_LINK,
        ];

      case MailTemplateType.SPERRUNG:
      case MailTemplateType.SPERRUNG_AUFGEHOBEN:
        return [
          MailTemplateVars.TITLE,
          MailTemplateVars.FIRST_NAME,
          MailTemplateVars.LAST_NAME,
          MailTemplateVars.LOGIN_LINK,
          MailTemplateVars.CHANGED_REGISTRATIONS,
          MailTemplateVars.CHANGED_REGISTRATIONS_EN,
        ];
      case MailTemplateType.ERINNERUNG:
        return [
          MailTemplateVars.TITLE,
          MailTemplateVars.FIRST_NAME,
          MailTemplateVars.LAST_NAME,
          MailTemplateVars.PRODUCT_CARD,
          MailTemplateVars.AUTOMATIC_MOVE_UP_DAYS,
          MailTemplateVars.BLOCK_SIGNOFF_DAYS,
          MailTemplateVars.LOGIN_LINK,
          MailTemplateVars.EVENT_LOCATION,
          MailTemplateVars.EVENT_DATES,
          MailTemplateVars.EVENT_LEAD_DAYS,
          MailTemplateVars.CHANGED_REGISTRATIONS,
          MailTemplateVars.CHANGED_REGISTRATIONS_EN,
          MailTemplateVars.EVALUATION_LINK,
          MailTemplateVars.EVALUATION_CODE,
        ];
      case MailTemplateType.ERINNERUNG_APPOINTMENT:
        return [
          MailTemplateVars.TITLE,
          MailTemplateVars.FIRST_NAME,
          MailTemplateVars.LAST_NAME,
          MailTemplateVars.PRODUCT_CARD,
          MailTemplateVars.APPOINTMENT_NAME,
          MailTemplateVars.APPOINTMENT_START,
          MailTemplateVars.APPOINTMENT_END,
          MailTemplateVars.APPOINTMENT_LOCATION,
          MailTemplateVars.LOGIN_LINK,
          MailTemplateVars.EVENT_DATES,
          MailTemplateVars.CHANGED_REGISTRATIONS,
          MailTemplateVars.CHANGED_REGISTRATIONS_EN,
          MailTemplateVars.EVALUATION_LINK,
          MailTemplateVars.EVALUATION_CODE,
        ];
      case MailTemplateType.MEETING_INFOS:
        return [
          MailTemplateVars.TITLE,
          MailTemplateVars.FIRST_NAME,
          MailTemplateVars.LAST_NAME,
          MailTemplateVars.MEETING_LINK,
          MailTemplateVars.PRODUCT_NAME,
          MailTemplateVars.PRODUCT_CARD,
          MailTemplateVars.EVALUATION_LINK,
          MailTemplateVars.EVALUATION_CODE,
        ];
      case MailTemplateType.OTP:
        return [
          MailTemplateVars.TITLE, //
          MailTemplateVars.FIRST_NAME,
          MailTemplateVars.LAST_NAME,
          MailTemplateVars.OTP,
        ];
      case MailTemplateType.FORGOT_PASSWORD:
        return [
          MailTemplateVars.TITLE,
          MailTemplateVars.FIRST_NAME,
          MailTemplateVars.LAST_NAME,
          MailTemplateVars.FORGOT_PASSWORD_LINK,
        ];
      case MailTemplateType.PRODUKT_LOESCHUNG:
        return [
          MailTemplateVars.TITLE,
          MailTemplateVars.FIRST_NAME,
          MailTemplateVars.LAST_NAME,
          MailTemplateVars.PRODUCT_NAME,
          MailTemplateVars.INSTITUTION_NAME,
          MailTemplateVars.LOGIN_LINK,
          MailTemplateVars.PRODUCT_CARD,
        ];
      case MailTemplateType.PRODUCT_CHANGED:
        return [
          MailTemplateVars.TITLE,
          MailTemplateVars.FIRST_NAME,
          MailTemplateVars.LAST_NAME,
          MailTemplateVars.CHANGED_REGISTRATIONS,
          MailTemplateVars.CHANGED_REGISTRATIONS_EN,
          MailTemplateVars.PRODUCT_CHANGES,
          MailTemplateVars.PRODUCT_CHANGES_EN,
          MailTemplateVars.LOGIN_LINK,
        ];
      case MailTemplateType.IKH_INVOICE_USER:
      case MailTemplateType.SD_INVOICE_USER:
      case MailTemplateType.DEMO_INVOICE_USER:
        return [
          MailTemplateVars.TITLE,
          MailTemplateVars.FIRST_NAME,
          MailTemplateVars.LAST_NAME,
          MailTemplateVars.WORKSHOP_NAME,
          MailTemplateVars.WORKSHOP_TERMINE,
          MailTemplateVars.WORKSHOP_LOCATION,
          MailTemplateVars.LOGIN_LINK,
        ];
      case MailTemplateType.IKH_INVOICE_INSTITUTION:
      case MailTemplateType.SD_INVOICE_INSTITUTION:
      case MailTemplateType.DEMO_INVOICE_INSTITUTION:
        return [
          MailTemplateVars.TITLE,
          MailTemplateVars.FIRST_NAME,
          MailTemplateVars.LAST_NAME,
          MailTemplateVars.WORKSHOP_NAME,
          MailTemplateVars.WORKSHOP_TERMINE,
          MailTemplateVars.WORKSHOP_LOCATION,
          MailTemplateVars.LOGIN_LINK,
        ];
      case MailTemplateType.LECTURER_REGISTRATION:
        return [
          MailTemplateVars.TITLE,
          MailTemplateVars.FIRST_NAME,
          MailTemplateVars.LAST_NAME,
          MailTemplateVars.LOGIN_LINK,
          MailTemplateVars.INSTITUTION_NAME,
        ];
      case MailTemplateType.LECTURER_ADDED_TO_PRODUCT:
        return [
          MailTemplateVars.TITLE,
          MailTemplateVars.FIRST_NAME,
          MailTemplateVars.LAST_NAME,
          MailTemplateVars.LOGIN_LINK,
          MailTemplateVars.PRODUCT_NAME,
          MailTemplateVars.INSTITUTION_NAME,
        ];
      case MailTemplateType.INFO_NEWSLETTER:
        return [
          MailTemplateVars.TITLE,
          MailTemplateVars.FIRST_NAME,
          MailTemplateVars.LAST_NAME,
          MailTemplateVars.LOGIN_LINK,
          MailTemplateVars.BOOKABLE_WORKSHOPS,
          MailTemplateVars.UNSUBSCRIBE_FROM_NEWSLETTER_LINK,
        ];
      case MailTemplateType.FREE_FORM:
        return [
          MailTemplateVars.TITLE,
          MailTemplateVars.FIRST_NAME,
          MailTemplateVars.LAST_NAME,
          MailTemplateVars.LOGIN_LINK,
          MailTemplateVars.INSTITUTION_NAME,
        ];
      case MailTemplateType.NEW_WORKSHOP_DOCUMENTS:
        return [
          MailTemplateVars.TITLE,
          MailTemplateVars.FIRST_NAME,
          MailTemplateVars.LAST_NAME,
          MailTemplateVars.LOGIN_LINK,
          MailTemplateVars.PRODUCT_NAME,
          MailTemplateVars.DOCUMENT_LINKS,
        ];
      default:
        return [];
    }
  }

  override async buildEmail(infos: MailVarContents = {}, buildMailOptions: Partial<BuildEmailOptions> = {}) {
    const globalSettings = await GlobalSetting.getGlobalSetting()({ emailBannerImageID: 1 });
    const newGlobalSetting = await Setting.getGlobalSettings()({
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
    });

    return super.buildEmail(infos, {
      footer: html``,
      ...(globalSettings.emailBannerImageID
        ? {
            logoConfig: {
              src: AdornisFile.getServeLink(globalSettings.emailBannerImageID!),
              alt: 'Logo',
            },
          }
        : {}),
      colorConfig: {
        mainBorder: 'none',
        mainBack: DesignSystem.currentTheme.getValue().colors.neutral.shade(50),
        wrapperBack: 'transparent',
        highlight:
          newGlobalSetting?.colorDefinitions?.accentColor ?? DesignSystem.currentTheme.getValue().colors.accent,
      },
      // ...buildMailOptions,
    });
  }

  @Query(type => [GlobalMailTemplate])
  static getGlobalMailTemplates() {
    return async (gqlFields: BaseQLSelectionSet<GlobalMailTemplate>) => {
      const existingGlobalMailTemplates = await (await getRawCollection<GlobalMailTemplate>(this._collectionName))
        .find({ _class: this._class })
        .toArray();

      const allTemplates = await Promise.all(
        Object.entries(mailTemplateDefinitions).map(async ([type, definition]) => {
          const existingGlobalMailTemplate = existingGlobalMailTemplates.find(
            mailTemplate => mailTemplate.emailType === type,
          );
          if (existingGlobalMailTemplate) {
            existingGlobalMailTemplate.scope = 'default';
            return existingGlobalMailTemplate;
          }

          const newMailTemplate = new GlobalMailTemplate({
            emailType: type,
            scope: 'default',
            name: definition.displayName,
            content: definition.defaultTemplate.content,
            subject: definition.defaultTemplate.subject,
            footer: definition.defaultTemplate.footer,
          });
          await newMailTemplate.create();

          return newMailTemplate;
        }),
      );

      return allTemplates;
    };
  }
}

@Entity()
export class InstitutionMailTemplate extends GlobalMailTemplate {
  static override _class = 'InstitutionMailTemplate';

  static override get allFields() {
    return {
      ...selectionSet(() => this, 2),
      institutionID: 1,
      institution: {
        name: 1,
        colorDefintions: selectionSet(() => ColorDefinitions, 3),
        emailBannerImageID: 1,
        settings: {
          colorDefinitions: selectionSet(() => ColorDefinitions, 4),
          inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
        },
        globalSettings: {
          colorDefintions: selectionSet(() => ColorDefinitions, 3),
        },
      },
      footer: 1,
    };
  }

  @Field(type => ID) institutionID?: string;
  @DataLoadedField(type => Institution, root => root.institutionID)
  institution: Maybe<Institution>;

  @Query(type => [InstitutionMailTemplate])
  static getMailTemplatesForInstitution(@Arg('institutionID', type => ID) institutionID: string) {
    return async (gqlFields: BaseQLSelectionSet<InstitutionMailTemplate>) => {
      if (!institutionID) throw new Error("'getMailTemplatesForInstitution' braucht eine institutionID");
      const allTemplates = await Promise.all(
        Object.entries(mailTemplateDefinitions).map(async ([type, definition]) => {
          return getInstitutionMailTemplateFromDefinitionAndCreateIfNotExists(institutionID, type, definition);
        }),
      );

      // filter out plugin emails
      return allTemplates.filter(template => {
        const mailType = template.emailType;
        const isPluginTemplate = mailType.startsWith('plugin::');
        if (!isPluginTemplate) return true;

        const paymentPlugin = getPluginForScope('payment', institutionID);
        return mailType.startsWith('plugin::' + paymentPlugin.id);
      });
    };
  }

  @Query(type => InstitutionMailTemplate)
  static getMailTemplateForInstitution(
    @Arg('institutionID', type => ID) institutionID: string,
    @Arg('type', type => String) type: MailTemplateType,
  ) {
    return async <SelSet extends BaseQLSelectionSet<InstitutionMailTemplate>>(gqlFields: SelSet) => {
      const definition = mailTemplateDefinitions[type];
      if (!definition) throw new Error('No definition found for type: ' + type);
      return getInstitutionMailTemplateFromDefinitionAndCreateIfNotExists(institutionID, type, definition);
    };
  }

  static mapFirstNameVar(user: NarrowedEntity<User, { firstName }>) {
    return user.firstName || '';
  }

  static mapLastNameVar(user: NarrowedEntity<User, { lastName }>) {
    return user.lastName || '';
  }

  static mapTime(time: Maybe<DateTime>) {
    if (!time) return '-';
    const parts = time.toLocaleParts();
    return `
      ${parts.filter(p => p.type === 'hour')[0]?.value}:${parts.filter(p => p.type === 'minute')[0]?.value}
    `;
  }

  static workshopTerminString(workshop: NarrowedEntity<Workshop, { appointments: { start: 1; end: 1 } }>) {
    const terminStrings = (workshop as Workshop).appointments
      .map(a => {
        if (!a.start || !a.end) return null;
        return `${a.start.toFormat('dd.LL.yyyy')} ${a.start.toFormat('HH:mm')} - ${a.end.toFormat('HH:mm')}`;
      })
      .filter(isDefined);

    if (!terminStrings.length) return '-';
    return terminStrings.join('; ');
  }

  static einzelberatungTerminString(
    product: NarrowedEntity<Einzelberatung, { slots: { start: 1; end: 1 } }>,
    timeslotID: string,
  ) {
    const slot = (product as Einzelberatung).slots.find(s => s._id === timeslotID);
    if (!slot?.start || !slot?.end) return '-';
    return `${slot.start.toFormat('dd.LL.yyyy')} ${slot.start.toFormat('HH:mm')} - ${slot.end.toFormat('HH:mm')}`;
  }

  static mapEventTitle(t: Product) {
    return t.name || '';
  }

  static mapChangedRegistrationVar(options: {
    productBefore: Maybe<NarrowedEntity<Product, { name: 1; topic: 1; language: 1 }>>;
    newState: ParticipationState;
    newWaitingListPlace: Maybe<number>;
    participation?: NarrowedEntity<
      Participation,
      {
        product: {
          slots: { _id: 1; start: 1; end: 1 };
          settings: { courseLocation: 1; inheritedCourseLocation: 1 };
          appointments: { start: 1; name: 1; end: 1; location: 1; meetingLink: { link: 1 } };
        };
        timeslotID: 1;
        appointments: { start: 1; name: 1; end: 1; location: 1 };
      }
    >;
    institution: NarrowedEntity<Institution, {}>;
    englishVersion: boolean;
  }) {
    return buildRegistrationsHTML(
      [
        {
          name: options.productBefore?.name ?? '',
          state: options.newState,
          topic: options.productBefore?.topic,
          language: options.productBefore?.language,
          waitingListPlace:
            options.newWaitingListPlace && options.newState === ParticipationState.WAITING
              ? options.newWaitingListPlace
              : undefined,
          institution: options.institution,
          appointments: getAppointmentsForParticipation(options.participation),
          englishVersion: options.englishVersion,
        },
      ],
      true,
    );
  }

  static mapOtherRegistrationsVar(options: {
    products: NarrowedEntity<Product, { _id: 1; name: 1; price: 1; hasPrice: 1; topic: 1; language: 1 }>[];
    participations: NarrowedEntity<Participation, { _id: 1; productID: 1; state: 1; waitingListPlace: 1 }>[];
    institution: NarrowedEntity<Institution, {}>;
    englishVersion: boolean;
  }) {
    const participationData = options.participations
      .map(participation => {
        const product = options.products.find(product => product._id === participation.productID);
        const appointments = (product as Workshop)?.appointments ?? [];
        if (appointments.length === 0) return undefined;
        return {
          name: product?.name ?? '',
          state: participation.state,
          topic: product?.topic,
          language: product?.language,
          institution: options.institution,
          waitingListPlace:
            participation.waitingListPlace && participation.state === ParticipationState.WAITING
              ? participation.waitingListPlace
              : undefined,
          appointments,
          englishVersion: options.englishVersion,
        };
      })
      .filter(isDefined);

    if (participationData.length === 0) {
      return '';
    }

    return buildRegistrationsHTML(participationData);
  }

  static mapProductVar(
    product: NarrowedEntity<
      Product,
      {
        name: 1;
        _class: 1;
        slotConfig: { start: 1; end: 1 };
        appointments: { start: 1; end: 1; location: 1 };
      }
    >,
  ) {
    const { start, end } = (() => {
      if (product._class === 'Workshop') return product as Workshop;
      if (product._class === 'Einzelberatung') return (product as Einzelberatung).slotConfig!;
      throw new Error('unreachable');
    })();
    if (!start) throw new Error('startdatum ist nicht gesetzt!');

    let dateIsYetToBeDetermined = (() => {
      if (product._class === 'Workshop') return (product as Workshop).dateIsToBeDetermined;
      return false;
    })();

    let location;
    if (product._class === 'Workshop') location = (product as Workshop).appointments[0]?.location;

    return buildProductCard(product.name, start, end, dateIsYetToBeDetermined, location);
  }

  static mapProductVarEinzelberatung(einzelberatung: Einzelberatung, slot: DateTime) {
    return buildProductCard(
      einzelberatung.name,
      slot,
      slot.plus({
        minutes: einzelberatung.slotConfig?.slotDuration ?? 15,
      }),
    );
  }

  static mapLoginLinkVar(token: string) {
    return buildLoginLink(token);
  }

  static mapOTPVar(code: string) {
    return buildOTP(code);
  }

  static mapConfirmMailVar(link: string) {
    return buildConfirmMail(link);
  }

  static mapForgotPassword(link: string) {
    return buildForgotPassword(link);
  }

  override async buildEmail(infos: MailVarContents = {}, buildMailOptions: Partial<BuildEmailOptions> = {}) {
    const params = this.institution ? new URLSearchParams({ institution: this.institutionID as string }) : undefined;
    const globalSettings = await GlobalSetting.getGlobalSetting()({ emailBannerImageID: 1 });

    return super.buildEmail(infos, {
      footer: html`
        <div class="section" style="margin-top: 20px;">
          <div style="width: 100%">
            ${this.institution ? html` <p>${this.institution.name}</p> ` : nothing}
            <div>
              <a
                style="text-decoration: none; font-size: 0.9em !important;"
                href=${`${A.absoluteUrl()}impressum?${params?.toString()}`}
              >
                Impressum
              </a>
            </div>
            <div>
              <a
                style="text-decoration: none; font-size: 0.9em !important;"
                href=${`${A.absoluteUrl()}datenschutz?${params?.toString()}`}
              >
                Datenschutz
              </a>
            </div>
          </div>
        </div>
      `,
      ...(this.institution?.emailBannerImageID || globalSettings.emailBannerImageID
        ? {
            logoConfig: {
              src: AdornisFile.getServeLink(this.institution?.emailBannerImageID || globalSettings.emailBannerImageID!),
              alt: 'Logo',
            },
          }
        : {}),
      colorConfig: {
        mainBorder: 'none',
        mainBack: DesignSystem.currentTheme.getValue().colors.neutral.shade(50),
        wrapperBack: 'transparent',
        highlight: this.institution?.colorDefintions.accentColor,
      },
      // ...buildMailOptions,
    });
  }
}

export type WhatsAppTemplate = {
  name: string;
};

export function getWhatsappTemplate(tempalte: MailTemplateType) {
  switch (tempalte) {
    case MailTemplateType.ABMELDUNG:
      return { name: 'abmeldung' };
    case MailTemplateType.ABMELDUNG_DURCH_EINRICHTUNG:
      return { name: 'abmeldung_durch_einrichtung' };
    case MailTemplateType.BESTAETIGUNG_WORKSHOP_REGISTRIERUNG:
      return { name: 'bestaetigung_workshop_registrierung' };
    case MailTemplateType.PRODUCT_CANCELED:
      return { name: 'product_canceled' };
    case MailTemplateType.PRODUCT_DECANCELED:
      return { name: 'product_decanceled' };
    case MailTemplateType.ERINNERUNG:
      return { name: 'erinnerung' };
    case MailTemplateType.ERINNERUNG_APPOINTMENT:
      return { name: 'erinnerung_appointment' };
    case MailTemplateType.MEETING_INFOS:
      return { name: 'meeting_infos' };
    case MailTemplateType.NACHRUECKPLATZ_VERFUEGBAR:
      return { name: 'nachrckplatz_verfuegbar' };
    case MailTemplateType.NACHRUECKPLATZ_ERHALTEN:
      return { name: 'nachrckplatz_erhalten2' };
    case MailTemplateType.NUTZER_TEILNAHME:
      return { name: 'nutzer_teilnahme' };
    case MailTemplateType.NUTZER_TEILNAHME_OHNE_ZERTIFIKAT:
      return { name: 'nutzer_teilnahme_no_attachment' };
    case MailTemplateType.SPERRUNG:
      return { name: 'sperrung_durch_einrichtung' };
    case MailTemplateType.SPERRUNG_AUFGEHOBEN:
      return { name: 'sperrung_aufgehoben' };
    case MailTemplateType.VERANSTALTUNGSEINTRAGUNG_DURCH_ADMIN:
      return { name: 'veranstaltungseintragung_durch_admin' };
    case MailTemplateType.NUTZER_TEILNAHME_AUFGEHOBEN:
      return { name: 'nutzer_teilnahme_aufgehoben' };
    case MailTemplateType.OTP:
      return { name: 'otp_code' };
    case MailTemplateType.Warteliste_VORRUECK:
      return { name: 'Warteliste_vorrueck' };
    case MailTemplateType.PRODUKT_LOESCHUNG:
      return { name: 'produkt_loeschung' };
    case MailTemplateType.PRODUCT_CHANGED:
      return { name: 'product_anderungen' };
    case MailTemplateType.IKH_INVOICE_USER:
      return { name: 'ikh_invoice_user' };
    case MailTemplateType.SD_INVOICE_USER:
      return { name: 'ikh_invoice_user' };
    case MailTemplateType.IKH_INVOICE_INSTITUTION:
      return { name: 'ikh_institution_invoice' };
    case MailTemplateType.SD_INVOICE_INSTITUTION:
      return { name: 'ikh_institution_invoice' };
    case MailTemplateType.NEW_WORKSHOP_DOCUMENTS:
    case MailTemplateType.NEWSLETTER_OPTIN:
      throw new Error('not implemented');
    default:
      return { name: 'default' };
  }
}

const shortCache = (() => {
  const cache = new Map<string, { createdAt: number; value: any }>();
  return {
    get: async <T>(key: string, ttl: number, createDefault: () => Promise<T>): Promise<T> => {
      if (cache.has(key) && new Date().getTime() - cache.get(key)!.createdAt < ttl) {
        return cache.get(key)!.value;
      }

      const value = await createDefault();
      cache.set(key, { createdAt: new Date().getTime(), value });
      return value;
    },
  };
})();

async function getInstitutionMailTemplateFromDefinitionAndCreateIfNotExists(
  institutionID: string,
  type: string,
  definition: MailTemplateDefinition,
) {
  const TEN_SECONDS = 1000 * 10;

  const existingInstitutionMailTemplates = await shortCache.get(
    `institutionMailTemplates-${institutionID}`,
    TEN_SECONDS,
    async () => {
      const c = await await getRawCollection<InstitutionMailTemplate>(InstitutionMailTemplate._collectionName);
      return c.find({ institutionID }).toArray();
    },
  );

  const existingInstitutionMailTemplate = existingInstitutionMailTemplates.find(
    mailTemplate => mailTemplate.emailType === type,
  );
  if (existingInstitutionMailTemplate) return existingInstitutionMailTemplate;

  const existingGlobalMailTemplates = await shortCache.get(`globalMailTemplates`, TEN_SECONDS, async () => {
    const c = await await getRawCollection<GlobalMailTemplate>(GlobalMailTemplate._collectionName);
    return c.find({ _class: GlobalMailTemplate._class }).toArray();
  });

  const existingGlobalMailTemplate = existingGlobalMailTemplates.find(mailTemplate => mailTemplate.emailType === type);

  if (existingGlobalMailTemplate) {
    const newMailTemplate = new InstitutionMailTemplate({
      ...existingGlobalMailTemplate,
      _id: null,
      _class: InstitutionMailTemplate._class,
      institutionID,
    });
    await newMailTemplate.create();
    return newMailTemplate;
  }

  const newMailTemplate = new InstitutionMailTemplate({
    emailType: type,
    institutionID,
    scope: 'default',
    name: definition.displayName,
    content: definition.defaultTemplate.content,
    subject: definition.defaultTemplate.subject,
    footer: definition.defaultTemplate.footer,
  });
  await newMailTemplate.create();

  return newMailTemplate;
}

export const subscribeMailTemplatesForInstitution = registerSubscription({
  operationName: 'subscribeMailTemplatesForInstitution',
  type: () => [InstitutionMailTemplate],
  params: [{ name: 'institutionID', type: () => String }],
  resolve: (institutionID: string) => {
    return (gqlFields: BaseQLSelectionSet<InstitutionMailTemplate>) => {
      if (!institutionID) throw new Error("'subscribeMailTemplatesForInstitution' braucht eine institutionID");

      return watchQuery(InstitutionMailTemplate._collectionName, { institutionID }).pipe(
        switchMap(() => InstitutionMailTemplate.getMailTemplatesForInstitution(institutionID)(gqlFields)),
      );
    };
  },
});
